import React, { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import './Modal.css'; // Add this import for the modal styles

/**
 * Reusable modal component for displaying content in a popup
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is currently open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.title - Title to display in the modal header
 * @param {React.ReactNode} props.children - Content to display in the modal body
 * @returns {JSX.Element|null} Rendered modal or null if closed
 */
const Modal = ({ isOpen, onClose, title, children }) => {
  /**
   * Handle keyboard events and body scroll when modal opens/closes
   */
  useEffect(() => {
    /**
     * Close the modal when Escape key is pressed
     * 
     * @param {KeyboardEvent} e - Keyboard event
     */
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent scrolling on body when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  /**
   * Close modal when clicking on backdrop (outside modal content)
   * 
   * @param {React.MouseEvent} e - Click event
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h3>{title}</h3>
          <Button 
            variant="close" 
            onClick={onClose}
            aria-label="Close"
          />
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
